/**
 * Date Utilities
 *
 * Centralized date/time utility functions that consolidate common patterns
 * found throughout the codebase. This module provides standardized functions
 * for the most frequently used date operations.
 */

// Re-export existing formatting utilities for convenience
export * from '../formatting';

// ============================================================================
// COMMON TIMESTAMP PATTERNS
// ============================================================================

/**
 * Get current ISO timestamp string
 * Replaces: new Date().toISOString()
 */
export function getCurrentISOTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Get current timestamp in milliseconds
 * Replaces: Date.now()
 */
export function getCurrentTimestamp(): number {
  return Date.now();
}

/**
 * Get current timestamp in seconds (for JWT and Unix timestamps)
 * Replaces: Math.floor(Date.now() / 1000)
 */
export function getCurrentTimestampSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

// ============================================================================
// DATE STRING PATTERNS
// ============================================================================

/**
 * Get current date as YYYY-MM-DD string in user's local timezone
 * IMPORTANT: Uses local timezone (getFullYear, getMonth, getDate) NOT UTC (toISOString)
 * This ensures date pickers show the correct date for the user's timezone
 * Replaces: new Date().toISOString().split('T')[0] (which used UTC timezone)
 */
export function getCurrentDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert date to YYYY-MM-DD string
 * Replaces: date.toISOString().split('T')[0]
 */
export function toDateString(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString().split('T')[0] || '';
}

/**
 * Create normalized date at start of day (00:00:00.000Z)
 * Replaces: new Date(date.toISOString().split('T')[0] + 'T00:00:00.000Z')
 */
export function toStartOfDay(date: Date | string): Date {
  const dateString = typeof date === 'string' ? date : toDateString(date);
  return new Date(dateString + 'T00:00:00.000Z');
}

/**
 * Create normalized date at end of day (23:59:59.999Z)
 */
export function toEndOfDay(date: Date | string): Date {
  const dateString = typeof date === 'string' ? date : toDateString(date);
  return new Date(dateString + 'T23:59:59.999Z');
}

// ============================================================================
// LOCALE-AWARE FORMATTING
// ============================================================================

/**
 * Format date for user's locale
 * Replaces: date.toLocaleDateString()
 * Replaces: date.toLocaleDateString(i18n.language)
 */
export function formatLocalDate(
  date: Date | string,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const userLocale = locale || getUserLocale();
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  return dateObj.toLocaleDateString(userLocale, defaultOptions);
}

/**
 * Format time for user's locale
 * Replaces: date.toLocaleTimeString()
 */
export function formatLocalTime(
  date: Date | string,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Time';
  }

  const userLocale = locale || getUserLocale();
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options
  };

  return dateObj.toLocaleTimeString(userLocale, defaultOptions);
}

/**
 * Format date with specific pattern for compact display
 * Replaces: date.toLocaleDateString('en-US', { month: 'short' })
 */
export function formatCompactDate(date: Date | string, locale?: string): string {
  return formatLocalDate(date, locale, {
    month: 'short',
    day: 'numeric'
  });
}

// ============================================================================
// TIME CALCULATIONS AND CUTOFFS
// ============================================================================

/**
 * Get timestamp for X hours ago
 * Replaces: Date.now() - (hours * 60 * 60 * 1000)
 */
export function getTimestampHoursAgo(hours: number): number {
  return Date.now() - (hours * 60 * 60 * 1000);
}

/**
 * Get timestamp for X days ago
 * Replaces: Date.now() - (days * 24 * 60 * 60 * 1000)
 */
export function getTimestampDaysAgo(days: number): number {
  return Date.now() - (days * 24 * 60 * 60 * 1000);
}

/**
 * Get date object for X hours ago
 * Replaces: new Date(Date.now() - hours * 60 * 60 * 1000)
 */
export function getDateHoursAgo(hours: number): Date {
  return new Date(getTimestampHoursAgo(hours));
}

/**
 * Get date object for X days ago
 * Replaces: new Date(Date.now() - days * 24 * 60 * 60 * 1000)
 */
export function getDateDaysAgo(days: number): Date {
  return new Date(getTimestampDaysAgo(days));
}

/**
 * Check if timestamp is within last X minutes
 * Replaces: timestamp > Date.now() - (minutes * 60 * 1000)
 */
export function isWithinLastMinutes(timestamp: number, minutes: number): boolean {
  return timestamp > Date.now() - (minutes * 60 * 1000);
}

/**
 * Check if timestamp is within last X hours
 * Replaces: timestamp > Date.now() - (hours * 60 * 60 * 1000)
 */
export function isWithinLastHours(timestamp: number, hours: number): boolean {
  return timestamp > Date.now() - (hours * 60 * 60 * 1000);
}

/**
 * Check if date is recent (within last hour)
 * Replaces: Date.now() - lastActivity.getTime() < 60 * 60 * 1000
 */
export function isRecent(date: Date | string, thresholdMinutes: number = 60): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return Date.now() - dateObj.getTime() < (thresholdMinutes * 60 * 1000);
}

// ============================================================================
// UNIQUE ID GENERATION WITH TIMESTAMPS
// ============================================================================

/**
 * Generate unique ID with timestamp prefix
 * Replaces: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
 */
export function generateTimestampId(prefix: string = 'id'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate session ID
 * Replaces: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
 */
export function generateSessionId(): string {
  return generateTimestampId('session');
}

/**
 * Generate audit ID
 * Replaces: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
 */
export function generateAuditId(): string {
  return generateTimestampId('audit');
}

/**
 * Generate error ID
 * Replaces: `ERR_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
 */
export function generateErrorId(): string {
  return `ERR_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate event ID
 * Replaces: `event-${Date.now()}`
 */
export function generateEventId(): string {
  return `event-${Date.now()}`;
}

/**
 * Generate temporary ID for new items
 * Replaces: `new-${Date.now()}`
 */
export function generateTempId(): string {
  return `new-${Date.now()}`;
}

// ============================================================================
// CACHE AND TTL UTILITIES
// ============================================================================

/**
 * Check if cache entry has expired
 * Replaces: Date.now() > (entry.timestamp + entry.ttl)
 */
export function isCacheExpired(timestamp: number, ttlMs: number): boolean {
  return Date.now() > (timestamp + ttlMs);
}

/**
 * Check if cache entry is still valid
 * Replaces: Date.now() <= (entry.timestamp + entry.ttl)
 */
export function isCacheValid(timestamp: number, ttlMs: number): boolean {
  return !isCacheExpired(timestamp, ttlMs);
}

/**
 * Get remaining TTL in milliseconds
 */
export function getRemainingTTL(timestamp: number, ttlMs: number): number {
  const remaining = (timestamp + ttlMs) - Date.now();
  return Math.max(0, remaining);
}

// ============================================================================
// PERFORMANCE TIMING
// ============================================================================

/**
 * Start performance timer
 * Replaces: const startTime = Date.now()
 */
export function startTimer(): number {
  return Date.now();
}

/**
 * Calculate elapsed time since start
 * Replaces: const responseTime = Date.now() - startTime
 */
export function getElapsedTime(startTime: number): number {
  return Date.now() - startTime;
}

/**
 * Create performance measurement object
 */
export function createPerformanceMeasurement(
  operation: string,
  startTime?: number
): { id: string; operation: string; startTime: number } {
  return {
    id: `${operation}-${Date.now()}-${Math.random()}`,
    operation,
    startTime: startTime || Date.now()
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get user's preferred locale (same as in formatting utils but re-exported for convenience)
 */
function getUserLocale(): string {
  if (typeof navigator !== 'undefined') {
    return navigator.language || 'en-US';
  }
  return 'en-US';
}